import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowSquareOutIcon, CurrencyDollarIcon, PlusIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import {
  ConfirmDelete,
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
} from "../../components/crm/Form";
import { FieldCard } from "../crm/CustomerDetail";
import StatusBadge from "../../components/ops/StatusBadge";
import PaymentRequestCard from "../../components/payments/PaymentRequestCard";
import RequestPaymentModal from "../../components/payments/RequestPaymentModal";
import {
  formatDollars,
  listPaymentRequestsForSR,
} from "../../lib/paymentRequests";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const STATUSES = ["new", "triage", "scheduled", "converted", "closed", "canceled"];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const SOURCES = ["phone", "website", "referral", "internal"];
const SERVICE_TYPES = [
  "diagnostic",
  "repair",
  "maintenance",
  "inspection",
  "upgrade",
  "other",
];
const optsFrom = (arr) =>
  arr.map((v) => ({ value: v, label: v.replace(/_/g, " ") }));

const TERMINAL = new Set(["converted", "closed", "canceled"]);

export default function ServiceRequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isOffice = ["owner", "ops"].includes(user?.role);
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [convertBusy, setConvertBusy] = useState(false);
  // Phase 10 — payment requests tied to this SR
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [paymentRequestOpen, setPaymentRequestOpen] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/service-requests/${id}`);
      setDoc(data);
      if (["owner", "ops"].includes(user?.role)) {
        try {
          const prs = await listPaymentRequestsForSR(id);
          setPaymentRequests(prs);
        } catch {
          // Silent — payment list is a read-only enhancement
          setPaymentRequests([]);
        }
      }
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const handleDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/service-requests/${id}`);
      nav("/service-requests");
    } catch (e) {
      setErr(formatApiError(e));
      setDelBusy(false);
    }
  };

  const convert = async () => {
    setErr("");
    // Phase 10 — soft gate. If any pending payment request exists, prompt
    // for confirmation. Ops can still override.
    const pending = paymentRequests.filter((pr) => pr.status === "pending");
    if (pending.length > 0) {
      const total = pending.reduce((s, pr) => s + (pr.amount_cents || 0), 0);
      const msg = `Payment hasn't been received yet (${formatDollars(total)} still pending). Dispatch anyway?`;
      if (!window.confirm(msg)) return;
    }
    setConvertBusy(true);
    try {
      const { data } = await api.post(`/service-requests/${id}/convert`);
      nav(`/work-orders/${data.id}`);
    } catch (e) {
      setErr(formatApiError(e));
      setConvertBusy(false);
    }
  };

  if (!doc)
    return (
      <Layout title="Service Request">
        <ErrorBanner message={err} />
      </Layout>
    );

  const canConvert = !TERMINAL.has(doc.status) && !doc.work_order_id;
  const pendingPayments = paymentRequests.filter((pr) => pr.status === "pending");
  const anyPaid = paymentRequests.some((pr) => pr.status === "paid");
  const pendingTotalCents = pendingPayments.reduce(
    (s, pr) => s + (pr.amount_cents || 0),
    0
  );

  return (
    <Layout title={doc.issue_summary} subtitle="Service request">
      <EntityHeader
        overline="service request"
        title={doc.issue_summary}
        breadcrumb={[
          { label: "Service Requests", to: "/service-requests" },
          { label: doc.issue_summary.slice(0, 40) },
        ]}
        meta={
          <>
            <StatusBadge value={doc.status} />
            <StatusBadge value={doc.priority} />
            <span>type · {doc.requested_service_type}</span>
            <span>source · {doc.intake_source}</span>
          </>
        }
        canEdit={true}
        canDelete={true}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDelOpen(true)}
      />

      <ErrorBanner message={err} />

      {(canConvert || doc.work_order_id) && (
        <div
          data-testid="sr-convert-panel"
          className="mb-6 bg-white border border-amber-200 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3"
        >
          <div>
            <div className="font-data text-[10px] uppercase tracking-[0.22em] text-amber-700">
              // dispatch
            </div>
            <div className="font-heading text-base font-semibold text-slate-900 mt-1">
              {doc.work_order_id
                ? "Already converted to a work order"
                : "Ready to convert this request into a work order?"}
            </div>
          </div>
          {doc.work_order_id ? (
            <Link
              to={`/work-orders/${doc.work_order_id}`}
              data-testid="sr-linked-wo-link"
              className="inline-flex items-center gap-2 rounded-sm bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
            >
              Open Work Order <ArrowSquareOutIcon size={12} weight="bold" />
            </Link>
          ) : (
            <PrimaryButton
              onClick={convert}
              disabled={convertBusy}
              data-testid="sr-convert-button"
              title={
                pendingPayments.length
                  ? `Payment of ${formatDollars(pendingTotalCents)} is pending. Dispatch after payment is confirmed.`
                  : undefined
              }
            >
              {convertBusy ? "Converting…" : "Convert to Work Order"}
            </PrimaryButton>
          )}
        </div>
      )}

      {isOffice && (
        <section
          data-testid="sr-payments-section"
          className="mb-6 bg-white border border-slate-200 rounded-md p-5 shadow-card"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon size={18} weight="duotone" className="text-emerald-600" />
              <h3 className="font-heading text-sm font-semibold text-slate-900">
                Payment
              </h3>
            </div>
            {!doc.work_order_id && (
              <PrimaryButton
                type="button"
                onClick={() => setPaymentRequestOpen(true)}
                data-testid="sr-request-payment-button"
              >
                <PlusIcon size={14} />
                {paymentRequests.length === 0 ? "Request Payment" : "Add another"}
              </PrimaryButton>
            )}
          </div>

          {anyPaid && (
            <div
              data-testid="sr-payment-ready-banner"
              className="mb-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800"
            >
              Payment received — ready to dispatch
            </div>
          )}

          {paymentRequests.length === 0 && (
            <div data-testid="sr-payments-empty" className="text-sm text-slate-500">
              No payment requests for this service call.
            </div>
          )}

          <div className="space-y-2" data-testid="sr-payments-list">
            {paymentRequests.map((pr) => (
              <PaymentRequestCard
                key={pr.id}
                pr={pr}
                onChange={(updated) =>
                  setPaymentRequests((list) =>
                    list.map((x) => (x.id === updated.id ? updated : x))
                  )
                }
              />
            ))}
          </div>
        </section>
      )}

      <RequestPaymentModal
        open={paymentRequestOpen}
        onClose={() => setPaymentRequestOpen(false)}
        serviceRequest={doc}
        onCreated={(pr) => setPaymentRequests((list) => [pr, ...list])}
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <FieldCard label="Customer" value={doc.customer_name || "—"} />
        <FieldCard
          label="Property"
          value={doc.property_address || "—"}
        />
        <FieldCard
          label="Preferred Date"
          value={doc.preferred_date || "—"}
          mono
        />
        <FieldCard
          label="Details"
          value={doc.issue_details || "—"}
          className="sm:col-span-2 lg:col-span-3"
        />
      </div>

      <EditServiceRequestModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        sr={doc}
        onSaved={(d) => {
          setEditOpen(false);
          setDoc(d);
        }}
      />
      <ConfirmDelete
        open={delOpen}
        onClose={() => setDelOpen(false)}
        onConfirm={handleDelete}
        label="Service Request"
        busy={delBusy}
      />
    </Layout>
  );
}

function EditServiceRequestModal({ open, onClose, sr, onSaved }) {
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        issue_summary: sr.issue_summary,
        issue_details: sr.issue_details || "",
        priority: sr.priority,
        status: sr.status,
        intake_source: sr.intake_source,
        requested_service_type: sr.requested_service_type,
        preferred_date: sr.preferred_date || "",
      });
      setErr("");
    }
  }, [open, sr]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.patch(`/service-requests/${sr.id}`, {
        issue_summary: form.issue_summary,
        issue_details: form.issue_details || null,
        priority: form.priority,
        status: form.status,
        intake_source: form.intake_source,
        requested_service_type: form.requested_service_type,
        preferred_date: form.preferred_date || null,
      });
      onSaved(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Service Request"
      subtitle="update record"
      data-testid="sr-edit-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Issue Summary" required>
          <TextInput
            required
            value={form.issue_summary || ""}
            onChange={(v) => setForm({ ...form, issue_summary: v })}
          />
        </Field>
        <Field label="Details">
          <TextArea
            value={form.issue_details || ""}
            onChange={(v) => setForm({ ...form, issue_details: v })}
            rows={4}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status">
            <Select
              value={form.status || ""}
              onChange={(v) => setForm({ ...form, status: v || "new" })}
              options={optsFrom(STATUSES)}
              placeholder=""
              data-testid="sr-edit-status"
            />
          </Field>
          <Field label="Priority">
            <Select
              value={form.priority || ""}
              onChange={(v) => setForm({ ...form, priority: v || "normal" })}
              options={optsFrom(PRIORITIES)}
              placeholder=""
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Source">
            <Select
              value={form.intake_source || ""}
              onChange={(v) =>
                setForm({ ...form, intake_source: v || "phone" })
              }
              options={optsFrom(SOURCES)}
              placeholder=""
            />
          </Field>
          <Field label="Service Type">
            <Select
              value={form.requested_service_type || ""}
              onChange={(v) =>
                setForm({
                  ...form,
                  requested_service_type: v || "diagnostic",
                })
              }
              options={optsFrom(SERVICE_TYPES)}
              placeholder=""
            />
          </Field>
        </div>
        <Field label="Preferred Date">
          <input
            type="date"
            value={form.preferred_date || ""}
            onChange={(e) =>
              setForm({ ...form, preferred_date: e.target.value })
            }
            className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="sr-edit-submit"
          >
            {busy ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
